import { useEffect, useState } from "react";
import { FiAlertCircle, FiFileText, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { extensionOf, formatBytes, kindOfFile } from "../utils/chatFiles";

/** One picked file: its picture, video frame or type, and how far its upload has got. */
function Tile({ item, onRemove, isDark, busy, compact }) {
  const { t } = useTranslation();
  const kind = kindOfFile(item.file);
  // Made and released by the same effect, so a re-run (Strict Mode) can't leave a dead URL.
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (kind === "file") return undefined;
    const made = URL.createObjectURL(item.file);
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [item.file, kind]);

  const size = compact ? "h-14 w-14" : "h-16 w-16";
  const uploading = busy && item.progress > 0 && !item.failed;

  return (
    <div className={`relative shrink-0 ${compact ? "w-14" : "w-16"}`} title={`${item.file.name} · ${formatBytes(item.file.size)}`}>
      <div
        className={`relative overflow-hidden rounded-radius-sm border ${size} ${
          item.failed ? "border-status-danger" : "border-separator-default bg-bg-raised"
        }`}
      >
        {kind === "image" && url && <img src={url} alt="" className="h-full w-full object-cover" />}
        {kind === "video" && url && <video src={url} muted playsInline preload="metadata" className="h-full w-full object-cover" />}
        {kind === "file" && (
          <div className={`flex h-full w-full flex-col items-center justify-center gap-0.5 text-label-secondary`}>
            <FiFileText className="h-5 w-5" />
            <span className="max-w-full truncate px-1 text-caption-1-semibold">{extensionOf(item.file.name) || t("chatFiles.file")}</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30">
            <div className="h-full bg-accent-default transition-[width]" style={{ width: `${Math.round(item.progress * 100)}%` }} />
          </div>
        )}
        {item.failed && (
          <div className="absolute inset-0 grid place-items-center bg-black/50">
            <FiAlertCircle className="h-5 w-5 text-status-danger" />
          </div>
        )}
      </div>
      <p className={`mt-0.5 truncate text-caption-1 text-label-secondary`}>{item.file.name}</p>
      {!busy && (
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          aria-label={t("chatFiles.remove")}
          title={t("chatFiles.remove")}
          className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-slate-900 text-white shadow ring-2 ring-white/80"
        >
          <FiX className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

/**
 * The files picked for the next message(s), shown above the box until they're sent. Each
 * goes as its own message; the text in the box is the first one's caption.
 */
const ChatPendingFiles = ({ items, onRemove, isDark, busy = false, compact = false }) => {
  const { t } = useTranslation();
  if (!items.length) return null;
  const failed = items.some((i) => i.failed);
  return (
    <div className={compact ? "mb-1.5" : "mb-3"}>
      <div className="flex gap-2.5 overflow-x-auto px-1 pb-1 pt-2 chat-scrollbar">
        {items.map((item) => (
          <Tile key={item.id} item={item} onRemove={onRemove} isDark={isDark} busy={busy} compact={compact} />
        ))}
      </div>
      <p className={`px-1 text-caption-1 ${failed ? "text-status-danger" : "text-label-secondary"}`}>
        {failed
          ? t("chatFiles.failedHint")
          : busy
            ? t("chatFiles.sending")
            : items.length === 1
              ? t("chatFiles.oneReady")
              : t("chatFiles.manyReady", { count: items.length })}
      </p>
    </div>
  );
};

export default ChatPendingFiles;
